import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useCadastroPesquisaItensCadastroPagination } from "../hooks/CadastroPesquisaItens_cadastro/pagination";
import { PesquisaSatisfacaoItemLista } from "../../domain/models/pesquisa_satisfacao_item_lista";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { PesquisaSatisfacaoItemForm } from "../schemas/CadastroPesquisaItens_cadastro/pesquisa_satisfacao_item";
import { PesquisaSatisfacaoDetalhes } from "../../domain/models/pesquisa_satisfacao_detalhes";

/**
 * LoadPesquisaSatisfacaoItensFuncState
 */

type LoadPesquisaSatisfacaoItensFuncState = {
  idPesquisaSatisfacao?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useCadastroPesquisaItensCadastroViewModel
 */

export const useCadastroPesquisaItensCadastroViewModel = () => {

  const {
    entregaObras: {
      getPesquisaSatisfacaoByIdUseCase,
      getAllPesquisaSatisfacaoItemUseCase,
      createPesquisaSatisfacaoItemUseCase,
      updatePesquisaSatisfacaoItemUseCase,
      deletePesquisaSatisfacaoItemUseCase,
    }
  } = useDependencyInjection();

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idPesquisaSatisfacao, setIdPesquisaSatisfacao] = useState<number>();

  const [pesquisaSatisfacao, setPesquisaSatisfacao] = useState<PesquisaSatisfacaoDetalhes>();

  const [pesquisaSatisfacaoItens, setPesquisaSatisfacaoItens] = useState<PesquisaSatisfacaoItemLista[]>();

  const [selectedPesquisaSatisfacaoItem, setSelectedPesquisaSatisfacaoItem] = useState<PesquisaSatisfacaoItemLista>();

  const [itemPesquisaSatisfacaoModal, setItemPesquisaSatisfacaoModal] = useState(false);
  const [itemPesquisaSatisfacaoDeleteModal, setItemPesquisaSatisfacaoDeleteModal] = useState(false);

  const pagination = useCadastroPesquisaItensCadastroPagination();

  /**
   * Funcoes de carregamento.
   */

  const loadPesquisaSatisfacao = useCallback(async (id?: number) => {
    try {
      const filteredIdPesquisaSatisfacao = id ?? idPesquisaSatisfacao;

      const results = await getPesquisaSatisfacaoByIdUseCase.getPesquisaSatisfacaoById(
        filteredIdPesquisaSatisfacao,
      );

      setPesquisaSatisfacao(results);

    } catch (e: unknown) {
      setPesquisaSatisfacao(undefined);
      throw new Error('Falha ao carregar itens de pesquisa de satisfação!');
    }
  }, [
    idPesquisaSatisfacao,
    getPesquisaSatisfacaoByIdUseCase,
  ]);

  const loadPesquisaSatisfacaoItens = useCallback(async (fs?: LoadPesquisaSatisfacaoItensFuncState) => {
    try {
      const filteredIdPesquisaSatisfacao = fs?.idPesquisaSatisfacao ?? idPesquisaSatisfacao;

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllPesquisaSatisfacaoItemUseCase.getAllPesquisaSatisfacaoItem(
        filteredIdPesquisaSatisfacao,
        filteredPage,
        filteredRowsPerPage,
      );

      setPesquisaSatisfacaoItens(getAllPesquisaSatisfacaoItemUseCase.items);
      pagination.setTotalItems(getAllPesquisaSatisfacaoItemUseCase.totalItems);
    } catch (e: unknown) {
      setPesquisaSatisfacaoItens(undefined);
      throw new Error('Falha ao carregar itens de pesquisa de satisfação!');
    }
  }, [
    idPesquisaSatisfacao,
    pagination,
    getAllPesquisaSatisfacaoItemUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const id = locationId ? Number(locationId) : null;

      if (!id) {
        throw new Error("Não foi possível obter o ID da pesquisa de satisfação!");
      }
      setIdPesquisaSatisfacao(id);

      await loadPesquisaSatisfacao(id);

      await loadPesquisaSatisfacaoItens({ idPesquisaSatisfacao: id });

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadPesquisaSatisfacao();

      await loadPesquisaSatisfacaoItens();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacao,
    loadPesquisaSatisfacaoItens,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/CadastroPesquisa_lista' });
  }, [
    backOrNavigate,
  ]);



  const handlePesquisaSatisfacaoItemOpenState = useCallback((idItemPesquisaSatisfacao: number | null, openState: boolean) => {
    if (openState) {
      if (idItemPesquisaSatisfacao && pesquisaSatisfacaoItens) {
        setSelectedPesquisaSatisfacaoItem(pesquisaSatisfacaoItens.find(item => item.idItemPesquisaSatisfacao === idItemPesquisaSatisfacao));
      } else {
        setSelectedPesquisaSatisfacaoItem(undefined);
      }
    }
    setItemPesquisaSatisfacaoModal(openState);
  }, [
    pesquisaSatisfacaoItens,
  ]);

  const handlePesquisaSatisfacaoItemSubmit = useCallback(async (form: PesquisaSatisfacaoItemForm) => {
    if (idPesquisaSatisfacao) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedPesquisaSatisfacaoItem) {
          await updatePesquisaSatisfacaoItemUseCase.updatePesquisaSatisfacaoItem(
            selectedPesquisaSatisfacaoItem.idItemPesquisaSatisfacao,
            form.ordem,
            form.descricao,
          );
        } else {
          await createPesquisaSatisfacaoItemUseCase.createPesquisaSatisfacaoItem(
            idPesquisaSatisfacao,
            form.ordem,
            form.descricao,
          );
        }

        await loadPesquisaSatisfacaoItens();

        setSelectedPesquisaSatisfacaoItem(undefined);
        setItemPesquisaSatisfacaoModal(false);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idPesquisaSatisfacao,
    selectedPesquisaSatisfacaoItem,
    createPesquisaSatisfacaoItemUseCase,
    updatePesquisaSatisfacaoItemUseCase,
    loadPesquisaSatisfacaoItens,
    setPageSaving,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handlePesquisaSatisfacaoItemDelete = useCallback((idItemPesquisaSatisfacao: number | null, openState: boolean) => {
    if (openState) {
      if (idItemPesquisaSatisfacao && pesquisaSatisfacaoItens) {
        setSelectedPesquisaSatisfacaoItem(pesquisaSatisfacaoItens.find(item => item.idItemPesquisaSatisfacao === idItemPesquisaSatisfacao));
      } else {
        setSelectedPesquisaSatisfacaoItem(undefined);
      }
    }
    setItemPesquisaSatisfacaoDeleteModal(openState);
  }, [
    pesquisaSatisfacaoItens,
  ]);

  const handlePesquisaSatisfacaoItemDeleteSubmit = useCallback(async () => {
    if (selectedPesquisaSatisfacaoItem) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deletePesquisaSatisfacaoItemUseCase.deletePesquisaSatisfacaoItem(
          selectedPesquisaSatisfacaoItem.idItemPesquisaSatisfacao,
        );

        await loadPesquisaSatisfacaoItens();

        setSelectedPesquisaSatisfacaoItem(undefined);
        setItemPesquisaSatisfacaoDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluído com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedPesquisaSatisfacaoItem,
    deletePesquisaSatisfacaoItemUseCase,
    loadPesquisaSatisfacaoItens,
    setPageSaving,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useCadastroPesquisaItensCadastroPagination.setState({ page: page + 1 });
      await loadPesquisaSatisfacaoItens({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoItens,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useCadastroPesquisaItensCadastroPagination.setState({ page: 1, rowsPerPage });
      await loadPesquisaSatisfacaoItens({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoItens,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    pesquisaSatisfacao: pesquisaSatisfacao ?? null,
    pesquisaSatisfacaoItens: pesquisaSatisfacaoItens ?? [],
    selectedPesquisaSatisfacaoItem: selectedPesquisaSatisfacaoItem ?? null,
    itemPesquisaSatisfacaoModal,
    itemPesquisaSatisfacaoDeleteModal,
    pagination,
    handleReload,
    handleBack,
    handlePesquisaSatisfacaoItemOpenState,
    handlePesquisaSatisfacaoItemSubmit,
    handlePesquisaSatisfacaoItemDelete,
    handlePesquisaSatisfacaoItemDeleteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
  };
}